import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type ApiProject,
  type ApiStyleguide,
} from "../lib/api";
import type { StyleHeadingSample } from "@clarity/shared";
import { ButtonSpinner, PageSpinner } from "../components/Loading";
import { toBreakdanceGlobalSettings } from "../lib/breakdance-global";
import { deriveRoleColors, type RoleColor } from "../lib/styleguide-roles";

type Load =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "ready"; project: ApiProject; styleguide: ApiStyleguide | null }
  | { kind: "error" };

type Gen = { busy: boolean; error: string | null };

export function StyleguidePage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [gen, setGen] = useState<Gen>({ busy: false, error: null });

  const load = useCallback(async () => {
    if (!id) return;
    setState({ kind: "loading" });
    try {
      const proj = await api.getProject(id);
      let styleguide: ApiStyleguide | null = null;
      try {
        const res = await api.getStyleguide(id);
        styleguide = res.styleguide;
      } catch (err) {
        if ((err as { status?: number }).status !== 404) throw err;
      }
      setState({ kind: "ready", project: proj.project, styleguide });
    } catch (err) {
      const status = (err as { status?: number }).status;
      setState(status === 404 ? { kind: "missing" } : { kind: "error" });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = useCallback(async () => {
    if (!id || gen.busy) return;
    setGen({ busy: true, error: null });
    try {
      const res = await api.generateStyleguide(id);
      setState((prev) =>
        prev.kind === "ready"
          ? { ...prev, styleguide: res.styleguide }
          : prev,
      );
      setGen({ busy: false, error: null });
    } catch {
      setGen({ busy: false, error: "Couldn’t generate. Try again." });
    }
  }, [id, gen.busy]);

  if (state.kind === "loading") return <PageSpinner label="Loading styleguide" />;
  if (state.kind === "missing") {
    return (
      <Wrap>
        <p className="text-neutral-600">That project doesn’t exist or was archived.</p>
        <Link to="/dashboard" className="mt-3 inline-block text-sm underline">
          Back to dashboard
        </Link>
      </Wrap>
    );
  }
  if (state.kind === "error") {
    return (
      <Wrap>
        <p className="text-neutral-600">Couldn’t load this styleguide.</p>
      </Wrap>
    );
  }

  const { project, styleguide } = state;

  return (
    <section className="mx-auto max-w-5xl px-6 py-10">
      <header className="flex items-start justify-between gap-4 border-b border-neutral-200 pb-6">
        <div>
          <Link
            to={`/projects/${project.id}`}
            className="text-xs text-neutral-500 hover:text-neutral-900"
          >
            ← {project.name}
          </Link>
          <h1 className="mt-2 text-2xl font-semibold">Styleguide</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Extracted from{" "}
            <a
              href={project.targetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-neutral-900"
            >
              {project.targetUrl}
            </a>
            {styleguide ? (
              <>
                {" · "}
                Last generated {formatAgo(styleguide.runAt)} from{" "}
                {styleguide.sourcePagesCount} page
                {styleguide.sourcePagesCount === 1 ? "" : "s"}
              </>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {styleguide ? (
            <>
              <BreakdanceExportButtons
                tokens={styleguide.tokens}
                projectName={project.name}
              />
              <button
                type="button"
                onClick={() => void generate()}
                disabled={gen.busy}
                className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-100 disabled:opacity-50"
              >
                {gen.busy ? <ButtonSpinner label="Analyzing…" /> : "Refresh"}
              </button>
            </>
          ) : null}
        </div>
      </header>

      {gen.error ? (
        <p className="mt-4 text-sm text-red-600">{gen.error}</p>
      ) : null}

      {styleguide ? (
        <StyleguideContent guide={styleguide} />
      ) : (
        <EmptyState busy={gen.busy} onGenerate={generate} />
      )}
    </section>
  );
}

function StyleguideContent({ guide }: { guide: ApiStyleguide }) {
  const { tokens } = guide;
  const roles = deriveRoleColors(tokens.colors);
  return (
    <div className="mt-8 space-y-12">
      <BrandAssetsSection
        faviconUrl={tokens.faviconUrl}
        logoUrl={tokens.logoUrl}
      />
      <ColorsSection roles={roles} palette={tokens.colors} />
      <ButtonsSection />
      <TypographySection
        fonts={tokens.fonts}
        sizes={tokens.sizes}
        weights={tokens.weights}
        headings={tokens.headings}
      />
      <FormsSection />
      <ContainersSection />
    </div>
  );
}

function BreakdanceExportButtons({
  tokens,
  projectName,
}: {
  tokens: ApiStyleguide["tokens"];
  projectName: string;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(toBreakdanceGlobalSettings(tokens));
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 1500);
    }
  }

  function download() {
    const json = toBreakdanceGlobalSettings(tokens);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugifyProjectName(projectName)}-breakdance-global-settings.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick so the click handler has time to grab the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void copy()}
        title="Copy a Breakdance Global Settings JSON to the clipboard"
        className={
          "rounded-md border px-3 py-1.5 text-sm transition " +
          (copyState === "copied"
            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
            : copyState === "failed"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100")
        }
      >
        {copyState === "copied"
          ? "Copied"
          : copyState === "failed"
            ? "Failed"
            : "Copy Breakdance JSON"}
      </button>
      <button
        type="button"
        onClick={download}
        title="Download a .json file ready for Breakdance → Settings → Global Settings → Import"
        className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100"
      >
        Download .json
      </button>
    </>
  );
}

function slugifyProjectName(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "project"
  );
}

function BrandAssetsSection({
  faviconUrl,
  logoUrl,
}: {
  faviconUrl: string | null;
  logoUrl: string | null;
}) {
  if (!faviconUrl && !logoUrl) return null;
  return (
    <Section title="Brand assets" subtitle="Logo and favicon found on the site.">
      <div className="flex flex-wrap gap-8">
        {logoUrl ? (
          <Tile label="Logo">
            <img
              src={logoUrl}
              alt="Logo"
              className="max-h-16 max-w-[14rem] object-contain"
            />
            <div className="mt-2 break-all text-[11px] text-neutral-500">
              {shortUrl(logoUrl)}
            </div>
          </Tile>
        ) : null}
        {faviconUrl ? (
          <Tile label="Favicon">
            <img
              src={faviconUrl}
              alt="Favicon"
              className="h-10 w-10 object-contain"
            />
            <div className="mt-2 break-all text-[11px] text-neutral-500">
              {shortUrl(faviconUrl)}
            </div>
          </Tile>
        ) : null}
      </div>
    </Section>
  );
}

function ColorsSection({
  roles,
  palette,
}: {
  roles: ReturnType<typeof deriveRoleColors>;
  palette: { value: string; count: number }[];
}) {
  if (palette.length === 0) {
    return (
      <Section title="Colors" subtitle="No colors detected.">
        <p className="text-sm text-neutral-500">
          The pages we analyzed didn’t expose recognizable color declarations.
        </p>
      </Section>
    );
  }
  return (
    <Section
      title="Colors"
      subtitle="Mirrors Breakdance → Global Settings → Colors. Role colors are heuristic guesses; the full palette is exported into Breakdance’s Palette."
    >
      <div className="space-y-8">
        <RoleColorRow label="Brand" role={roles.brand} />
        <RoleColorRow label="Text" role={roles.text} />
        <RoleColorRow label="Headings" role={roles.headings} />
        <RoleColorRow label="Links" role={roles.links} />
        <RoleColorRow label="Background" role={roles.background} />

        <div>
          <div className="mb-3 flex items-baseline justify-between">
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Palette
            </div>
            <div className="text-[11px] text-neutral-400">
              {palette.length} color{palette.length === 1 ? "" : "s"} · click to
              copy
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {palette.map((c) => (
              <ColorSwatch
                key={c.value}
                hex={c.value}
                count={c.count}
                tall
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function RoleColorRow({
  label,
  role,
}: {
  label: string;
  role: RoleColor | null;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-24 shrink-0 text-sm text-neutral-700">{label}</div>
      {role ? (
        <ColorSwatch hex={role.value} count={role.count} compact />
      ) : (
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-12 shrink-0 rounded-md border border-neutral-200"
            style={{
              backgroundImage:
                "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4), " +
                "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)",
              backgroundSize: "10px 10px",
              backgroundPosition: "0 0, 5px 5px",
            }}
            aria-hidden
          />
          <span className="text-[11px] text-neutral-400">
            Not derived — set manually in Breakdance
          </span>
        </div>
      )}
    </div>
  );
}

function ColorSwatch({
  hex,
  count,
  compact = false,
  tall = false,
}: {
  hex: string;
  count: number;
  compact?: boolean;
  tall?: boolean;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  async function copy() {
    try {
      await navigator.clipboard.writeText(hex);
      setState("copied");
      setTimeout(() => setState("idle"), 1200);
    } catch {
      setState("failed");
      setTimeout(() => setState("idle"), 1200);
    }
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => void copy()}
        title={`Click to copy ${hex}`}
        className="group relative inline-flex items-center gap-3 rounded-md border border-neutral-200 bg-white pl-1 pr-3 py-1 transition hover:border-neutral-400"
      >
        <span
          className="h-7 w-10 shrink-0 rounded"
          style={{ background: hex }}
          aria-hidden
        />
        <span className="font-mono text-xs text-neutral-700">{hex}</span>
        <span className="text-[10px] text-neutral-400">
          {count} use{count === 1 ? "" : "s"}
        </span>
        {state !== "idle" ? (
          <span
            className={
              "absolute -top-2 right-1 rounded-full px-1.5 text-[10px] font-semibold " +
              (state === "copied"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-red-100 text-red-700")
            }
          >
            {state === "copied" ? "Copied" : "Failed"}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={`Click to copy ${hex}`}
      className="group relative overflow-hidden rounded-md border border-neutral-200 bg-white text-left transition hover:border-neutral-400"
    >
      <div
        className={tall ? "h-20 w-full" : "h-14 w-full"}
        style={{ background: hex }}
        aria-hidden
      />
      <div className="px-2 py-1.5">
        <div className="font-mono text-[11px] text-neutral-700">{hex}</div>
        <div className="text-[10px] text-neutral-400">
          {count} use{count === 1 ? "" : "s"}
        </div>
      </div>
      {state !== "idle" ? (
        <span
          className={
            "absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm " +
            (state === "copied"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700")
          }
        >
          {state === "copied" ? "Copied" : "Failed"}
        </span>
      ) : null}
    </button>
  );
}

// Visual mirror of Breakdance → Global Settings → Buttons. Clarity does
// not extract button styles yet, so this is a structural placeholder so
// the owner knows what's in Breakdance and that they need to configure
// it there.
function ButtonsSection() {
  return (
    <Section
      title="Buttons"
      subtitle="Mirrors Breakdance → Global Settings → Buttons. Not auto-extracted; configure inside Breakdance."
    >
      <div className="space-y-2">
        <PlaceholderRow label="Primary" />
        <PlaceholderRow label="Secondary" />
        <PlaceholderRow label="Presets" />
      </div>
    </Section>
  );
}

function FormsSection() {
  return (
    <Section
      title="Forms"
      subtitle="Mirrors Breakdance → Global Settings → Forms. Not auto-extracted; configure inside Breakdance."
    >
      <div className="space-y-2">
        <PlaceholderRow label="Fields" />
        <PlaceholderRow label="Typography" />
        <PlaceholderRow label="Messages" />
        <PlaceholderRow label="Submit Button" />
      </div>
    </Section>
  );
}

function ContainersSection() {
  return (
    <Section
      title="Containers"
      subtitle="Mirrors Breakdance → Global Settings → Containers. Not auto-extracted; configure inside Breakdance."
    >
      <div className="space-y-2">
        <PlaceholderRow label="Sections" />
        <PlaceholderRow label="Column Gap" />
      </div>
    </Section>
  );
}

function PlaceholderRow({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-dashed border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
      <span className="text-neutral-700">{label}</span>
      <span className="text-[11px] text-neutral-400">
        Configure in Breakdance
      </span>
    </div>
  );
}

function SettingRow({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm">
      <span className="text-neutral-700">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="font-medium text-neutral-900">{value}</span>
        {note ? (
          <span className="text-[11px] text-neutral-400">{note}</span>
        ) : null}
      </span>
    </div>
  );
}

// Mirrors the heuristic in lib/breakdance-global.ts so the UI shows the
// same base size that gets exported. Picks the most-common px size in
// the body-text range (14–20px), falling back to 16px.
function pickBaseSizePx(sizes: { value: string; count: number }[]): number {
  const pxSizes = sizes
    .map((s) => {
      const m = /^(\d+(?:\.\d+)?)px$/.exec(s.value);
      if (!m) return null;
      const n = Number(m[1]);
      if (!Number.isFinite(n)) return null;
      return { n, count: s.count };
    })
    .filter((s): s is { n: number; count: number } => s !== null);
  const inRange = pxSizes.filter((s) => s.n >= 14 && s.n <= 20);
  const best = inRange[0] ?? pxSizes[0];
  return best ? Math.round(best.n) : 16;
}

function TypographySection({
  fonts,
  sizes,
  weights,
  headings,
}: {
  fonts: { family: string; count: number }[];
  sizes: { value: string; count: number }[];
  weights: { value: number; count: number }[];
  headings: StyleHeadingSample[];
}) {
  if (
    fonts.length === 0 &&
    sizes.length === 0 &&
    weights.length === 0 &&
    headings.length === 0
  ) {
    return null;
  }
  const fontStack =
    fonts.length > 0
      ? fonts.map((f) => quoteIfSpaces(f.family)).join(", ") +
        ", system-ui, sans-serif"
      : "inherit";
  const primaryFont = fonts[0]?.family ?? null;
  const baseSize = pickBaseSizePx(sizes);
  return (
    <Section
      title="Typography"
      subtitle="Mirrors Breakdance → Global Settings → Typography. Heading Font, Body Font, and Base Size flow into the Breakdance import."
    >
      <div className="space-y-8">
        <div className="space-y-2">
          <SettingRow
            label="Heading Font"
            value={primaryFont ?? "—"}
            note={primaryFont ? "Auto-filled on import" : "No font detected"}
          />
          <SettingRow
            label="Body Font"
            value={primaryFont ?? "—"}
            note={primaryFont ? "Auto-filled on import" : "No font detected"}
          />
          <SettingRow
            label="Base Size"
            value={`${baseSize} px`}
            note="Auto-filled on import"
          />
          <SettingRow
            label="Ratio"
            value="1.25"
            note="Breakdance default — adjust after import"
          />
          <SettingRow
            label="Advanced"
            value="Line heights — body 1.5, headings 1.2"
            note="Breakdance defaults — adjust after import"
          />
        </div>

        {fonts.length > 0 ? (
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              All font families observed
            </div>
            <div className="flex flex-wrap gap-2">
              {fonts.map((f) => (
                <span
                  key={f.family}
                  className="rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm"
                  style={{ fontFamily: quoteIfSpaces(f.family) }}
                >
                  {f.family}
                  <span className="ml-1.5 text-[11px] text-neutral-400">
                    {f.count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {headings.length > 0 ? (
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Headings sample
            </div>
            <div className="space-y-2 rounded-md border border-neutral-200 bg-white p-4">
              {headings.map((h) => (
                <div key={h.level} style={{ fontFamily: fontStack }}>
                  {h.level === 1 ? (
                    <h2 className="text-3xl font-bold">{h.text}</h2>
                  ) : h.level === 2 ? (
                    <h3 className="text-2xl font-semibold">{h.text}</h3>
                  ) : (
                    <h4 className="text-xl font-semibold">{h.text}</h4>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {sizes.length > 0 ? (
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Font sizes
            </div>
            <div className="flex flex-wrap gap-2 font-mono text-[11px]">
              {sizes.map((s) => (
                <span
                  key={s.value}
                  className="rounded-md border border-neutral-200 bg-white px-2 py-1"
                >
                  {s.value}
                  <span className="ml-1.5 text-neutral-400">{s.count}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {weights.length > 0 ? (
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Font weights
            </div>
            <div className="flex flex-wrap gap-2">
              {weights.map((w) => (
                <span
                  key={w.value}
                  className="rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm"
                  style={{
                    fontFamily: fontStack,
                    fontWeight: w.value,
                  }}
                >
                  {w.value}
                  <span className="ml-1.5 text-[11px] text-neutral-400">
                    {w.count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

function EmptyState({
  busy,
  onGenerate,
}: {
  busy: boolean;
  onGenerate: () => void;
}) {
  return (
    <div className="mt-12 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-6 py-12 text-center">
      <h2 className="text-base font-semibold">No styleguide yet</h2>
      <p className="mt-1 text-sm text-neutral-600">
        Avista will fetch every page in this project and surface the colors,
        fonts, and brand assets it finds. Takes a few seconds for small sites.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={busy}
        className="mt-6 rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-60"
      >
        {busy ? <ButtonSpinner label="Analyzing pages…" /> : "Generate styleguide"}
      </button>
    </div>
  );
}

// ---------- helpers ----------

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20 text-center">{children}</div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle ? (
          <p className="mt-0.5 text-sm text-neutral-500">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function Tile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-white p-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      {children}
    </div>
  );
}

function formatAgo(epochSeconds: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - epochSeconds));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86_400) return `${Math.floor(diff / 86_400)}d ago`;
  return new Date(epochSeconds * 1000).toLocaleDateString();
}

function shortUrl(url: string): string {
  if (url.length <= 60) return url;
  return url.slice(0, 30) + "…" + url.slice(-25);
}

function quoteIfSpaces(family: string): string {
  return /\s/.test(family) ? `"${family}"` : family;
}
